import React, { useState, useEffect } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, Mail, MessageSquare, CalendarCheck, TrendingUp, DollarSign, Send, Inbox, ClipboardCheck, FileEdit } from 'lucide-react';
import { api } from '../../context/AuthContext';

const MetricCard = ({ icon: Icon, label, value, color = 'text-slate-200' }) => (
  <Card className="bg-slate-900 border-slate-800">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold font-mono mt-1 ${color}`} data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
        </div>
        <Icon className="w-8 h-8 text-slate-700" strokeWidth={1} />
      </div>
    </CardContent>
  </Card>
);

export default function BackofficeDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/backoffice/metrics/overview')
      .then(r => setMetrics(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <BackofficeLayout>
      <div data-testid="backoffice-dashboard">
        <h1 className="text-xl font-bold text-white tracking-tight mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
          OPERATIONS OVERVIEW
        </h1>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="bg-slate-900 border-slate-800 animate-pulse h-24" />
            ))}
          </div>
        ) : metrics ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <MetricCard icon={Users} label="Leads (7d)" value={metrics.leads_created_7d} color="text-blue-400" />
            <MetricCard icon={Mail} label="Contacted (7d)" value={metrics.contacted_7d} color="text-orange-400" />
            <MetricCard icon={MessageSquare} label="Replies (7d)" value={metrics.replies_7d} color="text-emerald-400" />
            <MetricCard icon={CalendarCheck} label="Booked (7d)" value={metrics.booked_7d} color="text-sky-400" />
            <MetricCard icon={TrendingUp} label="Conversions (30d)" value={metrics.conversions_30d} color="text-green-400" />
            <MetricCard icon={DollarSign} label="Pipeline Value" value={`£${metrics.pipeline_value_estimate.toLocaleString()}`} color="text-amber-400" />
          </div>
        ) : (
          <p className="text-slate-500">Failed to load metrics.</p>
        )}

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Pipeline Status Widgets */}
          <a href="/backoffice/outbox" className="block" data-testid="widget-outbox">
            <Card className="bg-slate-900 border-slate-800 hover:border-slate-600 transition-colors">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <FileEdit className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono text-amber-400">{metrics?.drafts_pending_approval || 0}</p>
                  <p className="text-xs text-slate-500">Pending Approvals</p>
                </div>
              </CardContent>
            </Card>
          </a>
          <a href="/backoffice/inbox" className="block" data-testid="widget-inbox">
            <Card className="bg-slate-900 border-slate-800 hover:border-slate-600 transition-colors">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Inbox className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono text-blue-400">{metrics?.replies_unhandled || 0}</p>
                  <p className="text-xs text-slate-500">Unhandled Replies</p>
                </div>
              </CardContent>
            </Card>
          </a>
          <a href="/backoffice/tasks" className="block" data-testid="widget-tasks">
            <Card className="bg-slate-900 border-slate-800 hover:border-slate-600 transition-colors">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <ClipboardCheck className="w-5 h-5 text-red-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono text-red-400">{metrics?.tasks_due_today || 0}</p>
                  <p className="text-xs text-slate-500">Tasks Due Today</p>
                </div>
              </CardContent>
            </Card>
          </a>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400 uppercase tracking-wider">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <a href="/backoffice/prospects" className="block px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-sm transition-colors" data-testid="qa-prospects">
                View all prospects
              </a>
              <a href="/backoffice/audit-inbox" className="block px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-sm transition-colors" data-testid="qa-audit">
                Check audit inbox
              </a>
              <a href="/backoffice/pipeline" className="block px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-sm transition-colors" data-testid="qa-pipeline">
                View pipeline
              </a>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400 uppercase tracking-wider">System Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-400">
                <span>Outbound Sending</span>
                <span className="text-red-400 font-mono text-xs">DISABLED</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>AI Provider</span>
                <span className="text-amber-400 font-mono text-xs">MOCK</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </BackofficeLayout>
  );
}
